/**
 * PJ から開いた詳細画面で、変更後が未存在だったときに「この PJ が消した」のか「元から無い」のかを
 * 決める判定。
 *
 * PJ の変更一覧は削除した業務・用語・総合テストへもリンクを張る。変更後の面で引くと消した
 * ノードは必ず未存在になるので、判定を持たない画面は削除行を開くと「見つかりません」に落ちる。
 * 読み手はそこで**消えた中身を見られず、削除が意図どおりかを確かめる手段を失う**。変更前
 * （マスター）に居れば、それは PJ が消したノードとして描く。
 *
 * 判定を各詳細画面に散らさないのは、6 画面が同じ分岐（未存在・待ち・削除・失敗）を持つためで
 * ある。写すと、どれか 1 画面で「変更前の取得中」を未存在と読み違えても型は通る。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ から開いた詳細の変更後が未存在のとき、PJ が消したノードか元から無いノードかを変更前の取得結果から決める。
 * @graph-connects isNotFoundError [calls] 未存在による失敗かの判定
 */
import { isNotFoundError } from './orpc-errors.js';

/**
 * 変更後が未存在だったときの扱い。
 *
 * - `present`: 変更後が未存在ではない。画面は通常どおり描く
 * - `waiting`: 変更後は未存在だが、変更前がまだ返っていない
 * - `deleted`: 変更前に居る。この PJ が消したノードとして描く
 * - `absent`: 変更前にも居ない（または PJ を選んでいない）。未存在として描く
 * - `beforeFailed`: 変更前の取得が未存在以外で失敗した。失敗として描く
 */
export type PjDeletionResolution =
  | { kind: 'present' }
  | { kind: 'waiting' }
  | { kind: 'deleted' }
  | { kind: 'absent' }
  | { kind: 'beforeFailed'; error: unknown };

/** `resolvePjDeletion` の入力。 @graph-connects none */
export interface PjDeletionInput {
  /** PJ を選んで開いているか。 */
  pjMode: boolean;
  /** 変更後（PJ を選んでいなければ現在版）の取得の失敗。 */
  afterError: unknown;
  /**
   * 変更後に居ないことが、取得の前に分かっているか（省けば分かっていない）。
   *
   * **PJ が消したことは案件の変更の行に書いてある**（変更後の無い行）。それを知っている画面は
   * 変更後を引かない — 引けば必ず未存在で返り、押していない利用者のコンソールと通信の記録に
   * 404 が 1 本残る。引かずに知った場合も、引いて未存在で返った場合と同じ振り分けに畳む。
   * 畳まないと、引かずに済ませた面だけが変更後の到着を永久に待つ。
   */
  afterKnownMissing?: boolean;
  /** 変更前（マスター）の取得結果。 */
  before: { data: unknown; error: unknown };
}

/**
 * 変更後が未存在だったときに、PJ が消したノードかを決める。
 *
 * **変更前が返るまで未存在と言わない。** 変更後の 404 は変更前より先に返ることが多く、そこで
 * 未存在と描くと、消したノードが一瞬「見つかりません」になってから削除の画面に入れ替わる。
 *
 * @param input PJ の有無と、変更後の失敗・変更前の取得結果
 * @returns 変更後が未存在だったときの扱い
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変更後の未存在を、変更前の取得結果から「PJ が消した・元から無い・待ち・失敗」に振り分ける。
 * @graph-connects isNotFoundError [calls] 未存在による失敗かの判定
 */
export function resolvePjDeletion({
  pjMode,
  afterError,
  afterKnownMissing = false,
  before,
}: PjDeletionInput): PjDeletionResolution {
  if (!afterKnownMissing && !isNotFoundError(afterError)) return { kind: 'present' };
  if (!pjMode) return { kind: 'absent' };
  if (before.data !== undefined) return { kind: 'deleted' };
  if (before.error === null || before.error === undefined) return { kind: 'waiting' };
  if (isNotFoundError(before.error)) return { kind: 'absent' };
  return { kind: 'beforeFailed', error: before.error };
}
